import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { store } from '@/store/store';
import { hubClient } from '@tfpl/api-client';
import { getFeedbackHeatmapRange, getFeedbackHeatmapWeeks } from '@/utils';
import { getConfig } from '@/config';
import { isoUTCFormat } from '@/utils/dateFormats';
import settingsModule from '@/store/modules/SettingsModule';

export interface Feedback {
  id: number;
  heatmapWeekId: number;
}

export interface WeekBoundary {
  weekStart: Date;
  weekEnd: Date;
}

export interface WeekBoundaryWithFeedbacks {
  weekStart: Date;
  weekEnd: Date;
  userFeedback?: UserFeedback;
}

export interface UserFeedback {
  id: number;
  fullName: string;
  userUuid: string;
  value: hubClient.CollaboratorFeedbackDto['value'];
  fromDate: Date;
  toDate: Date;
}

@Module({ name: 'HeatmapModule', store, dynamic: true, namespaced: true })
export class HeatmapModule extends VuexModule {
  /**************************************************************************
   * STATE
   *************************************************************************/
  private _loading = false;
  private _error = false;
  private _userFeedbacks: UserFeedback[] = [];
  private _myFeedbacks: UserFeedback[] = [];
  private _feedbacksWeeks: WeekBoundary[] = [];

  /**************************************************************************
   * GETTERS
   *************************************************************************/
  get userFeedbacks(): UserFeedback[] {
    return this._userFeedbacks;
  }

  get myFeedbacks(): WeekBoundaryWithFeedbacks[] {
    return this._feedbacksWeeks
      .map((w) => {
        return {
          ...w,
          userFeedback: this._myFeedbacks.find((f) => isoUTCFormat(f.fromDate) === isoUTCFormat(w.weekStart)),
        };
      })
      .toReversed();
  }

  get feedbackWeeks(): WeekBoundary[] {
    return this._feedbacksWeeks;
  }

  get loading(): boolean {
    return this._loading;
  }

  get error(): boolean {
    return this._error;
  }

  /**************************************************************************
   * MUTATIONS
   *************************************************************************/
  @Mutation
  clearUserFeedbacks(): void {
    this._userFeedbacks = [];
  }

  @Mutation
  setUserFeedbacks(userFeedbacks: UserFeedback[]): void {
    this._userFeedbacks = userFeedbacks;
  }

  @Mutation
  setMyFeedbacks(myFeedbacks: UserFeedback[]): void {
    this._myFeedbacks = myFeedbacks;
  }

  @Mutation
  setFeedbackWeeks(feedbackWeeks: WeekBoundary[]): void {
    this._feedbacksWeeks = feedbackWeeks;
  }

  @Mutation
  setLoading(loading: boolean): void {
    this._loading = loading;
  }

  @Mutation
  setError(error: boolean): void {
    this._error = error;
  }

  /**************************************************************************
   * ACTIONS
   *************************************************************************/
  @Action({ rawError: true })
  public async fetchUserFeedbacks({
    userUuid = undefined,
    fromDate = undefined,
    toDate = undefined,
  }: {
    userUuid?: string;
    fromDate?: string;
    toDate?: string;
  }): Promise<void> {
    this.setLoading(true);
    try {
      const monthBoundaries = getFeedbackHeatmapRange(settingsModule.settings.feedbackCrontab);
      let response: {
        data: Array<hubClient.CollaboratorFeedbackDto>;
      } & hubClient.OkManyResponse;
      if (userUuid) {
        const { data } = await hubClient.V3CollaboratorFeedbacksService.getSingleV3({
          path: { tenant: getConfig().TENANT, company: getConfig().TENANT, collaboratorUuid: userUuid },
          query: {
            fromDate: fromDate ?? isoUTCFormat(monthBoundaries.firstDay),
            toDate: toDate ?? isoUTCFormat(monthBoundaries.lastDay),
          },
          throwOnError: true,
        });
        response = data;
      } else {
        const { data } = await hubClient.V3CollaboratorFeedbacksService.getManyV3({
          path: { tenant: getConfig().TENANT, company: getConfig().TENANT },
          query: {
            fromDate: fromDate ?? isoUTCFormat(monthBoundaries.firstDay),
            toDate: toDate ?? isoUTCFormat(monthBoundaries.lastDay),
          },
          throwOnError: true,
        });
        response = data;
      }

      const responseFeedbacks: UserFeedback[] = response.data.map((item) => {
        return {
          id: item.id,
          fullName: item.collaborator.name,
          userUuid: item.collaborator.uuid,
          value: item.value as hubClient.CollaboratorFeedbackDto['value'],
          fromDate: new Date(item.fromDate),
          toDate: new Date(item.toDate),
        };
      });
      this.setUserFeedbacks(responseFeedbacks);

      const weeks = getFeedbackHeatmapWeeks(settingsModule.settings.feedbackCrontab);
      this.setFeedbackWeeks(weeks);
    } catch (err) {
      this.setError(true);
      console.error(err);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  public async fetchMyFeedbacks({ companyCode }: { companyCode: string }): Promise<void> {
    this.setLoading(true);
    try {
      const monthBoundaries = getFeedbackHeatmapRange(settingsModule.settings.feedbackCrontab);
      const { data: response } = await hubClient.V3CollaboratorFeedbacksService.getMyFeedbacksV3({
        path: { tenant: companyCode, company: companyCode },
        query: {
          fromDate: isoUTCFormat(monthBoundaries.firstDay),
          toDate: isoUTCFormat(monthBoundaries.lastDay),
        },
        throwOnError: true,
      });

      const responseFeedbacks: UserFeedback[] = response.data.map((item) => {
        return {
          id: item.id,
          fullName: item.collaborator.name,
          userUuid: item.collaborator.uuid,
          value: item.value as hubClient.CollaboratorFeedbackDto['value'],
          fromDate: new Date(item.fromDate),
          toDate: new Date(item.toDate),
        };
      });
      this.setMyFeedbacks(responseFeedbacks);

      const weeks = getFeedbackHeatmapWeeks(settingsModule.settings.feedbackCrontab);
      this.setFeedbackWeeks(weeks);
    } catch (err) {
      this.setError(true);
      console.error(err);
    } finally {
      this.setLoading(false);
    }
  }
}
export default getModule(HeatmapModule);
